<template>
  <div class="rb-ob-form rb-match-page rb-session-page">
    <header>
      <h1 class="rb-ob-title">{{ $t('intake.onlineSession.checkTitle') }}</h1>
    </header>

    <div class="rb-session-outcome">
      <p>{{ $t('intake.onlineSession.checkBody') }}</p>
    </div>

    <p v-if="needsLogin" class="rb-ob-status" role="status">{{ $t('intake.portal.needsLogin') }}</p>
    <p v-else-if="!loading && !session" class="rb-ob-status" role="status">{{ $t('intake.onlineSession.noSession') }}</p>

    <ul v-if="session" class="rb-session-meta" :aria-label="$t('intake.onlineSession.waitMetaAria')">
      <li>
        <span class="rb-session-meta-k">{{ $t('intake.onlineSession.waitTherapist') }}</span>
        <span class="rb-session-meta-v">{{ therapistName }}</span>
      </li>
      <li>
        <span class="rb-session-meta-k">{{ $t('intake.onlineSession.waitTime') }}</span>
        <span class="rb-session-meta-v">{{ sessionTime }}</span>
      </li>
      <li>
        <span class="rb-session-meta-k">{{ $t('intake.onlineSession.roomConn') }}</span>
        <span class="rb-session-meta-v">{{ joinReady ? $t('intake.onlineSession.joinReady') : $t('intake.onlineSession.joinPending') }}</span>
      </li>
    </ul>

    <div class="rb-session-check-grid" role="list">
      <div v-for="row in checks" :key="row.key" class="rb-session-check-item" role="listitem">
        <span class="rb-session-check-k">{{ $t('intake.onlineSession.' + row.key) }}</span>
        <span :class="['rb-session-check-v', row.okClass]">{{ row.label }}</span>
      </div>
    </div>

    <div class="rb-session-stage rb-session-av-stage">
      <div
          class="rb-session-video rb-session-av-preview"
          :class="{'is-live': previewActive, 'is-waiting': !previewActive}"
      >
        <video
            v-show="previewActive"
            ref="preview"
            class="rb-session-video-el"
            playsinline
            muted
            autoplay
        />
        <span v-if="previewActive" class="rb-session-badge">
          {{ $t('intake.onlineSession.checkPreviewBadge') }}
        </span>
        <div v-else class="rb-session-av-placeholder" role="status">
          <p class="rb-session-av-placeholder-title">
            {{ $t('intake.onlineSession.checkPreviewWaitingTitle') }}
          </p>
          <p class="rb-session-av-placeholder-body">
            {{ $t('intake.onlineSession.checkPreviewWaitingBody') }}
          </p>
        </div>
      </div>
    </div>

    <div class="rb-ob-actions">
      <button
          class="rb-ob-submit"
          type="button"
          :disabled="!session"
          @click="enterWait"
      >
        {{ $t('intake.onlineSession.checkEnterWait') }}
      </button>
      <button
          v-if="mediaPending || media.mic === false || media.cam === false"
          class="rb-ob-submit rb-ob-submit-secondary"
          type="button"
          :disabled="probing"
          @click="retryProbe"
      >
        {{ $t('intake.onlineSession.checkRetry') }}
      </button>
      <button class="rb-ob-submit rb-ob-submit-secondary" type="button" @click="showHelp">
        {{ $t('intake.onlineSession.checkHelp') }}
      </button>
      <p class="rb-ob-status" role="status" aria-live="polite">{{ formStatus }}</p>
      <div class="rb-ob-trust">
        <p>{{ $t('intake.trustPath') }}</p>
        <p>{{ $t('authUi.familyNote') }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import {
  doctorDisplayName,
  formatSessionListTitle,
  hasAuthToken,
  resolveJoinableSession,
} from '@/intake/portalApi'

const PROBE_TIMEOUT_MS = 12000

export default {
  name: 'SessionAvCheckStep',
  data() {
    return {
      loading: true,
      needsLogin: false,
      session: null,
      formStatus: '',
      probing: false,
      media: {mic: null, cam: null},
      previewStream: null,
      probeGeneration: 0,
      disposed: false,
    }
  },
  computed: {
    lang() {
      return this.$route.params.lang || 'fa'
    },
    therapistName() {
      return doctorDisplayName(this.session && this.session.doctor, this.lang) || '—'
    },
    sessionTime() {
      try {
        return formatSessionListTitle(this.session, this.lang) || '—'
      } catch (e) {
        return '—'
      }
    },
    joinReady() {
      return !!(this.session && this.session.can_enter)
    },
    mediaPending() {
      return this.media.mic === null || this.media.cam === null
    },
    previewActive() {
      return !!(this.previewStream && this.media.cam === true)
    },
    checks() {
      const ok = (v) => (v === true ? 'is-ok' : (v === false ? 'is-warn' : ''))
      const label = (v, okKey, badKey, unkKey) => {
        if (v === true) return this.$t('intake.onlineSession.' + okKey)
        if (v === false) return this.$t('intake.onlineSession.' + badKey)
        return this.$t('intake.onlineSession.' + unkKey)
      }
      return [
        {key: 'checkMic', okClass: ok(this.media.mic), label: label(this.media.mic, 'checkMicOk', 'checkMicBad', 'checkPending')},
        {key: 'checkCam', okClass: ok(this.media.cam), label: label(this.media.cam, 'checkCamOk', 'checkCamBad', 'checkPending')},
        {key: 'checkSpeaker', okClass: 'is-ok', label: this.$t('intake.onlineSession.checkSpeakerOk')},
        {key: 'checkNet', okClass: navigator.onLine ? 'is-ok' : 'is-warn', label: navigator.onLine ? this.$t('intake.onlineSession.checkNetOk') : this.$t('intake.onlineSession.checkNetBad')},
      ]
    },
  },
  mounted() {
    this.bootstrap()
    this.probeMedia()
  },
  beforeDestroy() {
    this.disposed = true
    this.cancelProbe()
  },
  methods: {
    bootstrap() {
      this.loading = true
      this.needsLogin = !hasAuthToken()
      const preferred = this.$route.query.id || this.$route.params.sessionId
      resolveJoinableSession(preferred)
          .then((session) => {
            this.session = session
          })
          .finally(() => {
            this.loading = false
          })
    },
    stopStream(stream) {
      if (!stream || typeof stream.getTracks !== 'function') return
      stream.getTracks().forEach(track => { try { track.stop() } catch (_) { /* already stopped */ } })
    },
    cancelProbe() {
      this.probeGeneration += 1
      this.probing = false
      this.stopPreview()
    },
    withTimeout(promise, ms, generation) {
      return new Promise((resolve, reject) => {
        let settled = false
        const timer = setTimeout(() => {
          settled = true
          const err = new Error('media probe timed out')
          err.name = 'TimeoutError'
          reject(err)
        }, ms)
        promise.then(
            (value) => {
              clearTimeout(timer)
              // getUserMedia cannot be cancelled. Even an ignored resolution
              // owns live tracks, so dispose it after timeout or navigation.
              if (settled || this.disposed || generation !== this.probeGeneration) {
                this.stopStream(value)
                if (!settled) reject(new Error('MEDIA_PROBE_CANCELLED'))
                settled = true
                return
              }
              settled = true
              resolve(value)
            },
            (err) => {
              clearTimeout(timer)
              settled = true
              reject(err)
            },
        )
      })
    },
    stopPreview() {
      if (this.previewStream) {
        this.stopStream(this.previewStream)
        this.previewStream = null
      }
      const el = this.$refs.preview
      if (el) {
        el.srcObject = null
      }
    },
    attachPreview(stream, generation) {
      if (this.disposed || generation !== this.probeGeneration) {
        this.stopStream(stream)
        return
      }
      this.stopPreview()
      this.previewStream = stream
      this.$nextTick(() => {
        if (this.disposed || generation !== this.probeGeneration || this.previewStream !== stream) return
        const el = this.$refs.preview
        if (el && stream) {
          el.srcObject = stream
          const play = el.play()
          if (play && typeof play.catch === 'function') {
            play.catch(() => {
              // Autoplay can be blocked; muted+playsinline usually works.
            })
          }
        }
      })
    },
    async probeMedia() {
      if (this.probing || this.disposed) {
        return
      }
      this.probing = true
      const generation = ++this.probeGeneration
      const active = () => !this.disposed && generation === this.probeGeneration
      this.formStatus = ''
      this.media = {mic: null, cam: null}
      this.stopPreview()

      if (typeof window !== 'undefined' && window.isSecureContext === false) {
        this.media = {mic: false, cam: false}
        this.formStatus = this.$t('intake.onlineSession.checkInsecure')
        this.probing = false
        return
      }
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.media = {mic: false, cam: false}
        this.formStatus = this.$t('intake.onlineSession.checkUnsupported')
        this.probing = false
        return
      }

      try {
        const stream = await this.withTimeout(
            navigator.mediaDevices.getUserMedia({audio: true, video: true}),
            PROBE_TIMEOUT_MS,
            generation,
        )
        this.attachPreview(stream, generation)
        this.media = {mic: true, cam: true}
        this.formStatus = ''
      } catch (combinedErr) {
        if (!active()) return
        let mic = false
        let cam = false
        try {
          const audio = await this.withTimeout(
              navigator.mediaDevices.getUserMedia({audio: true}),
              PROBE_TIMEOUT_MS,
              generation,
          )
          mic = true
          audio.getTracks().forEach((t) => t.stop())
        } catch (audioErr) {
          mic = false
        }

        if (!active()) return

        try {
          const video = await this.withTimeout(
              navigator.mediaDevices.getUserMedia({video: true}),
              PROBE_TIMEOUT_MS,
              generation,
          )
          cam = true
          this.attachPreview(video, generation)
        } catch (videoErr) {
          cam = false
        }

        if (!active()) return
        this.media = {mic, cam}
        if (!mic || !cam) {
          this.formStatus = this.$t('intake.onlineSession.checkHelpStatus')
        }
      } finally {
        if (generation === this.probeGeneration) this.probing = false
      }
    },
    retryProbe() {
      this.probeMedia()
    },
    enterWait() {
      // AV check is advisory for local entry; do not hard-block waiting room.
      this.cancelProbe()
      const q = this.session && this.session.id ? {id: String(this.session.id)} : {}
      this.$router.push({
        name: 'intakeSessionWait',
        params: {lang: this.lang},
        query: q,
      })
    },
    showHelp() {
      this.formStatus = this.$t('intake.onlineSession.checkHelpStatus')
    },
  },
}
</script>
